import { type BaseItem } from '../common.js';

export interface BaseJob {
  payload: object;
  priority: number;
  practiceId: string;
  providerUserId: string | null;
  ehr: string;
  totalAttempts: number | null;
  completedTimestamp: Date | null;
  completedBy: string | null;
  ehrInstanceId: string | null;
  succeeded: boolean | null;
  kind: JobKind | null;
}

export interface Job extends BaseItem, BaseJob {}

export enum JobKind {
  // Syncs
  AppointmentUpdateAggregation = 'appointmentUpdateAggregation',
  CalendarSync = 'calendarSync',
  OutreachSync = 'outreachSync',
  PatientListSync = 'patientListSync',
  // Exports
  ExportAllergies = 'exportAllergies',
  ExportAppointmentNote = 'exportAppointmentNote',
  ExportAppointmentStatus = 'exportAppointmentStatus',
  ExportAuthorizationForm = 'exportAuthorizationForm',
  ExportAuthorizationStart = 'exportAuthorizationStart',
  ExportAuthorizationStatus = 'exportAuthorizationStatus',
  ExportAuthorizationSubmission = 'exportAuthorizationSubmission',
  ExportBilling = 'exportBilling',
  ExportBillingGroup = 'exportBillingGroup',
  ExportCareGapFindings = 'exportCareGapFindings',
  ExportClinicalComment = 'exportClinicalComment',
  ExportComposition = 'exportComposition',
  ExportDummyValues = 'exportDummyValues',
  ExportNewEncounter = 'exportNewEncounter',
  ExportEncounterInformation = 'exportEncounterInformation',
  ExportEncounterPatientData = 'exportEncounterPatientData',
  ExportFamilyMedicalHistory = 'exportFamilyMedicalHistory',
  ExportFinancialResponsibility = 'exportFinancialResponsibility',
  ExportFindingEvidence = 'exportFindingEvidence',
  ExportFollowUp = 'exportFollowUp',
  ExportFormOutcomes = 'exportFormOutcomes',
  ExportForms = 'exportForms',
  ExportGeneralComment = 'exportGeneralComment',
  ExportGuarantors = 'exportGuarantors',
  ExportHccFindings = 'exportHccFindings',
  ExportHospitalAccount = 'exportHospitalAccount',
  ExportMarkAsReviewed = 'exportMarkAsReviewed',
  ExportMedications = 'exportMedications',
  ExportNote = 'exportNote',
  ExportNoticeOfAdmission = 'exportNoticeOfAdmission',
  ExportNoticeOfAdmissionStatus = 'exportNoticeOfAdmissionStatus',
  ExportPatientInformation = 'exportPatientInformation',
  ExportPatientInsurance = 'exportPatientInsurance',
  ExportPatientMedicalHistory = 'exportPatientMedicalHistory',
  ExportPatientOutreaches = 'exportPatientOutreaches',
  ExportPatientUploads = 'exportPatientUploads',
  ExportPatientUploadStatuses = 'exportPatientUploadStatuses',
  ExportPcp = 'exportPcp',
  ExportPharmacy = 'exportPharmacy',
  ExportPrenatalFlowsheet = 'exportPrenatalFlowsheet',
  ExportPreregistrationEncounter = 'exportPreregistrationEncounter',
  ExportPrevisitPlanning = 'exportPrevisitPlanning',
  ExportProblemGroups = 'exportOrders',
  ExportOrderRequestListStatus = 'exportOrderRequestListStatus',
  ExportProcessedPayments = 'exportProcessedPayments',
  ExportProcessedRefunds = 'exportProcessedRefunds',
  ExportQualityMeasures = 'exportQualityMeasures',
  ExportReferralDocument = 'exportReferralDocument',
  ExportReferralOrder = 'exportReferralOrder',
  ExportRegistrationNote = 'exportRegistrationNote',
  ExportScheduledAppointment = 'exportScheduledAppointment',
  ExportScheduledOrderAssociation = 'exportScheduledOrderAssociation',
  ExportSignOff = 'exportSignOff',
  ExportSocialHistory = 'exportSocialHistory',
  ExportSurgicalHistory = 'exportSurgicalHistory',
  ExportTerminatedInsurances = 'exportTerminatedInsurances',
  ExportVerifyRegistration = 'exportVerifyRegistration',
  // Extracts
  ExtractAllergies = 'extractAllergies',
  ExtractAuthorizationStatus = 'extractAuthorizationStatus',
  ExtractBilling = 'extractBilling',
  ExtractCareGapFindings = 'extractCareGapFindings',
  ExtractCareGapStatuses = 'extractCareGapStatuses',
  ExtractCarryCurrent = 'extractCarryCurrent',
  ExtractCarryForward = 'extractCarryForward',
  ExtractEncounter = 'extractEncounter',
  ExtractEncounterInformation = 'extractEncounterInformation',
  ExtractEncounterPatientData = 'extractEncounterPatientData',
  ExtractEpisodeOb = 'extractEpisodeOb',
  ExtractFamilyMedicalHistory = 'extractFamilyMedicalHistory',
  ExtractGuarantors = 'extractGuarantors',
  ExtractHistoricalIngestion = 'extractHistoricalIngestion',
  ExtractHistoricalPatientEncounters = 'extractHistoricalPatientEncounters',
  ExtractImmunizationRegistry = 'extractImmunizationRegistry',
  ExtractLabValues = 'extractLabValues',
  ExtractMedicalNecessity = 'extractMedicalNecessity',
  ExtractMedications = 'extractMedications',
  ExtractMspqForm = 'extractMspqForm',
  ExtractNote = 'extractNote',
  ExtractNoticeOfAdmissionStatus = 'extractNoticeOfAdmissionStatus',
  ExtractOutstandingBalances = 'extractOutstandingBalances',
  ExtractPatientAdvisories = 'extractPatientAdvisories',
  ExtractPatientAdvisoryDocuments = 'extractPatientAdvisoryDocuments',
  ExtractPatientCommunicationConsent = 'extractPatientCommunicationConsent',
  ExtractPatientDocuments = 'extractPatientDocuments',
  ExtractPatientEligibility = 'extractPatientEligibility',
  ExtractPatientInformation = 'extractPatientInformation',
  ExtractPatientInsurance = 'extractPatientInsurance',
  ExtractPatientInsuranceRteInformation = 'extractPatientInsuranceRteInformation',
  ExtractPatientMedicalHistory = 'extractPatientMedicalHistory',
  ExtractPatientOrder = 'extractPatientOrder',
  ExtractPatientProblems = 'extractPatientProblems',
  ExtractPatientUploads = 'extractPatientUploads',
  ExtractPcp = 'extractPcp',
  ExtractPharmacy = 'extractPharmacy',
  ExtractPredictBillingData = 'extractPredictBillingData',
  ExtractPregnancy = 'extractPregnancy',
  ExtractProblemGroups = 'extractProblemGroups',
  ExtractQualityMeasures = 'extractQualityMeasures',
  ExtractSocialHistory = 'extractSocialHistory',
  ExtractSurgicalHistory = 'extractSurgicalHistory',
  ExtractVitals = 'extractVitals',
  FetchInterpretations = 'fetchInterpretations',
  Reckon = 'reckon',
  RotateCredential = 'rotateCredential',
}
